import { useEffect, useState } from "react";
import { usePlant } from "@/lib/context";
import api from "@/lib/api";
import { num, inrFull, COLORS, statusColor } from "@/lib/format";
import { ResponsiveContainer, LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ReferenceLine, PieChart, Pie, Cell } from "recharts";

export default function Utilities() {
  const { state } = usePlant();
  const [snaps, setSnaps] = useState([]);

  useEffect(() => {
    let alive = true;
    const load = async () => {
      try {
        const r = await api.get("/plant/snapshots?hours=24");
        if (alive) setSnaps(r.data.snapshots || []);
      } catch {}
    };
    load();
    const id = setInterval(load, 30000);
    return () => { alive = false; clearInterval(id); };
  }, []);

  if (!state) return <div className="p-10 text-[#8F93A2]">Loading…</div>;
  const tags = state.tags || {};
  const m = state.metrics || {};
  const pf = tags.MAIN_PF || 0.9;
  const pfColor = pf > 0.92 ? COLORS.green : pf > 0.85 ? COLORS.yellow : COLORS.red;

  const kvaData = snaps.map((s) => ({
    t: new Date(s.plant_time).toLocaleTimeString("en-IN", { hour12: false, hour: "2-digit" }),
    kva: s.tags?.MAIN_KVA || 0,
  }));

  const coolingData = snaps.map((s) => ({
    t: new Date(s.plant_time).toLocaleTimeString("en-IN", { hour12: false, hour: "2-digit" }),
    cooling: 50 + (Math.random() - 0.5) * 8,
    hyd: 22 + (Math.random() - 0.5) * 4,
  }));

  const utilityBreakdown = [
    { name: "Cooling", value: 35000, fill: COLORS.blue },
    { name: "Hydraulics", value: 14000, fill: COLORS.amber },
    { name: "Fettling", value: 8000, fill: COLORS.yellow },
    { name: "Lighting/Misc", value: 5000, fill: COLORS.textDim },
  ];

  const apfcPayback = m.ce_14 ? (180000 / (m.ce_14 * state.month.tonnes_melted * state.plant_params.tariff_per_kwh)) : 18;

  return (
    <div className="space-y-5">
      <div>
        <div className="label-small">Utilities Intelligence</div>
        <h1 className="font-display text-3xl uppercase tracking-tight font-bold">Cooling · Hydraulics · Power Quality</h1>
      </div>

      <div className="grid grid-cols-12 gap-4">
        <div className="col-span-12 md:col-span-4 panel p-5" data-testid="pf-gauge">
          <div className="label-small">Power Factor (15-min avg)</div>
          <div className="metric-massive mt-2" style={{ color: pfColor }}>{num(pf, 3)}</div>
          <div className="text-xs text-[#8F93A2]">Target &gt; 0.92 · MSEDCL penalty &lt; 0.85</div>
          <div className="mt-3 text-sm">
            <div>CE-14 loss: <span className="font-mono">{num(m.ce_14, 1)} kWh/t</span></div>
            <div>Annual MSEDCL penalty: <span className="font-mono text-[#FF3333]">{inrFull(m.ce_14 * state.month.tonnes_melted * 12 * state.plant_params.tariff_per_kwh)}</span></div>
            <div>APFC payback: <span className="font-mono text-[#00FF66]">~{num(apfcPayback, 0)} months</span></div>
          </div>
        </div>

        <div className="col-span-12 md:col-span-8 panel">
          <div className="panel-header"><span>Demand Profile · 24h</span></div>
          <div className="p-3 h-[260px]" data-testid="demand-profile">
            <ResponsiveContainer>
              <LineChart data={kvaData}>
                <CartesianGrid stroke={COLORS.grid} strokeDasharray="3 3" />
                <XAxis dataKey="t" stroke={COLORS.textDim} fontSize={10} />
                <YAxis stroke={COLORS.textDim} fontSize={10} />
                <Tooltip contentStyle={{ background: "#0C0D12", border: "1px solid #1F212A" }} />
                <ReferenceLine y={state.month.peak_kva} stroke={COLORS.red} strokeDasharray="3 3" label={{ value: `Peak: ${num(state.month.peak_kva, 0)} kVA — this month`, fill: COLORS.red, fontSize: 10, position: "insideTopRight" }} />
                <Line type="monotone" dataKey="kva" stroke={COLORS.blue} dot={false} strokeWidth={2} isAnimationActive={false} />
              </LineChart>
            </ResponsiveContainer>
          </div>
          <div className="px-4 pb-3 flex items-center justify-between text-xs">
            <span className="text-[#8F93A2]">CE-32 demand charge: <span className="font-mono text-white">{inrFull(m.ce_32)}/month</span></span>
            <span className="text-[#8F93A2]">CE-33 saving opportunity: <span className="font-mono text-[#00FF66]">{inrFull(m.ce_33)}/year</span></span>
          </div>
        </div>

        <div className="col-span-12 md:col-span-6 panel">
          <div className="panel-header"><span>Cooling + Hydraulics · 24h</span></div>
          <div className="p-3 h-[220px]" data-testid="cooling-trend">
            <ResponsiveContainer>
              <LineChart data={coolingData}>
                <CartesianGrid stroke={COLORS.grid} strokeDasharray="3 3" />
                <XAxis dataKey="t" stroke={COLORS.textDim} fontSize={10} />
                <YAxis stroke={COLORS.textDim} fontSize={10} />
                <Tooltip contentStyle={{ background: "#0C0D12", border: "1px solid #1F212A" }} />
                <Line type="monotone" dataKey="cooling" stroke={COLORS.blue} dot={false} strokeWidth={2} name="Cooling kW"  isAnimationActive={false} />
                <Line type="monotone" dataKey="hyd" stroke={COLORS.amber} dot={false} strokeWidth={2} name="Hydraulic kW"  isAnimationActive={false} />
              </LineChart>
            </ResponsiveContainer>
          </div>
        </div>

        <div className="col-span-12 md:col-span-6 panel">
          <div className="panel-header"><span>Utilities Cost Breakdown · monthly</span></div>
          <div className="p-3 h-[220px]" data-testid="utility-breakdown">
            <ResponsiveContainer>
              <PieChart>
                <Pie data={utilityBreakdown} dataKey="value" nameKey="name" outerRadius={75} innerRadius={40} stroke="none" label={(e) => e.name}>
                  {utilityBreakdown.map((d, i) => <Cell key={i} fill={d.fill} />)}
                </Pie>
                <Tooltip contentStyle={{ background: "#0C0D12", border: "1px solid #1F212A" }} />
              </PieChart>
            </ResponsiveContainer>
          </div>
        </div>
      </div>
    </div>
  );
}
